/**
 * The numbers and message lines the candidate briefing measures against,
 * copied from the Canales for District 8 playbook (Elevate El Paso,
 * 2026-10-18). Change them here only when the playbook changes.
 *
 * Shared BY COPY with functions/src/notify/targets.ts (functions never import
 * web code); functions/test/notify-sender.test.js fails if they drift, so
 * change both together.
 */

/** Election day, District 8 general (public record). Day keys are America/Denver dates. */
export const ELECTION_DAY = "2026-11-03";

/**
 * Runoff day if no candidate clears 50%. PROJECTED from the 2018 (Dec 15),
 * 2022 (Dec 17) and 2024 (Dec 14) precedent; the council sets the date by
 * resolution after the canvass.
 */
export const RUNOFF_DAY = "2026-12-12";

export interface MoneyTarget {
  /** Stable id for i18n labels (`brief.target.<id>`). */
  id: string;
  /** The day the cumulative figure must be reached (campaign time zone). */
  day: string;
  raisedCents: number;
  cashOnHandCents: number;
}

/**
 * Cumulative raised and cash on hand at each date (§10.2, §10.3, §11.7).
 *
 * The playbook states the money goals as NEW money on top of the $2,300
 * already reported for calendar 2026; these rows carry that $2,300 forward so
 * they can be compared against the scoreboard's cumulative figure:
 *   Oct 24 — the decision trigger's $3,000 floor of new money
 *   Oct 26 — the $8,000 low end of the $8,000-$12,000 eight-day-report target
 *   Dec 5  — the $6,000 low end of the $6,000-$8,000 runoff raise
 * Cash on hand is the one hard floor the playbook sets: the $4,000 December
 * reserve, preserved at all times.
 */
export const MONEY_TARGETS: readonly MoneyTarget[] = [
  { id: "oct24", day: "2026-10-24", raisedCents: 530_000, cashOnHandCents: 400_000 },
  { id: "oct26", day: "2026-10-26", raisedCents: 1_030_000, cashOnHandCents: 400_000 },
  { id: "dec05", day: "2026-12-05", raisedCents: 1_630_000, cashOnHandCents: 400_000 },
];

/** §2.2 operational targets: hard supporter IDs by November 2. */
export const SUPPORTER_TARGET = 4_500;

/**
 * Candidate call time per week, and the pledged-dollars-per-hour goal.
 * 0 = not set: the playbook commits the candidate to ten personal asks a
 * weekday before noon (§10.2) and states no hours figure and no dollars-per-
 * hour goal anywhere. Nothing here may be invented, so these stay 0 until the
 * campaign sets them; the console works unchanged while they are 0.
 */
export const CALL_HOURS_PER_WEEK = 0;
export const CALL_CENTS_PER_HOUR = 0;

/**
 * §11.7 decision trigger T-CASH: under $3,000 in new money on October 24
 * cancels all paid digital, cuts to literature and texting, and raises the
 * December reserve to $5,000. The threshold carries the $2,300 already
 * reported, as the money targets above do.
 */
export const MONEY_TRIGGER = { day: "2026-10-24", thresholdCents: 530_000 } as const;

/** A scoreboard figure older than this many days is flagged as stale. */
export const STALE_AFTER_DAYS = 7;

export interface MessageLine {
  id: string;
  en: string;
  es: string;
  /** True when the Spanish is our working translation, not playbook text. */
  workingTranslation: boolean;
}

/**
 * §3.1 core message and §3.2 Pillar 3 (both languages are playbook text), plus
 * the cleared contrast and defense lines the candidate practises. Spanish
 * marked as a working translation is ours, not the playbook's.
 */
export const MESSAGE_LINES: readonly MessageLine[] = [
  {
    id: "core",
    en: "I'm the only person in this race who has actually taken something off your bill. I put the arena bonds on the ballot and you revoked a hundred and twenty-eight million dollars. I got the Flock cameras ordered down. I wrote the policy that stops the next data center before it starts. And on the budget, I'm going to give you the truth instead of a slogan: I voted yes, it's about eight dollars and seventy-five cents a month on an average home, it pays firefighters and streets, and I'll tell you to your face why I did it. Sixteen days. Let's finish it.",
    es: "Soy la única persona en esta contienda que de verdad le ha quitado algo a su recibo. Puse los bonos de la arena en la boleta y ustedes revocaron ciento veintiocho millones de dólares. Logré que ordenaran quitar las cámaras Flock. Escribí la política que frena el próximo centro de datos antes de que empiece. Y sobre el presupuesto, les voy a decir la verdad en lugar de un eslogan: voté que sí, son unos ocho dólares con setenta y cinco centavos al mes en una casa de valor promedio, paga bomberos y calles, y les voy a decir en la cara por qué lo hice. Faltan dieciséis días. Vamos a terminarlo.",
    workingTranslation: false,
  },
  {
    id: "attendance",
    en: "I've been at ninety-five percent of the votes this council has taken in four years, and ninety-eight percent of them this year. Whatever else you think of me, I show up.",
    es: "He estado presente en el noventa y cinco por ciento de las votaciones de este concejo en cuatro años, y en el noventa y ocho por ciento este año. Piensen lo que piensen de mí, yo me presento.",
    workingTranslation: false,
  },
  {
    id: "doorstep-tax",
    en: "Before you ask me anything — yes, I voted for the budget this year, and yes, that's about eight dollars and seventy-five cents a month on an average home. I'm not going to pretend I didn't. Here's why I did it: the firefighters were owed a contract, the debt payments came due, and the state cut what we're allowed to collect. The choice in front of me was that, or cut a fire academy. I'd rather tell you the truth and lose your vote than lie and keep it. Now — what's going on with your street?",
    es: "Antes de que me pregunten nada: sí, voté por el presupuesto este año, y sí, son unos ocho dólares con setenta y cinco centavos al mes en una casa de valor promedio. No voy a fingir que no lo hice. Les digo por qué lo hice: a los bomberos se les debía un contrato, vencieron los pagos de la deuda, y el estado recortó lo que podemos cobrar. La opción frente a mí era esa, o recortar una academia de bomberos. Prefiero decirles la verdad y perder su voto que mentirles y conservarlo. Ahora, ¿qué está pasando con su calle?",
    workingTranslation: true,
  },
  {
    id: "flock",
    en: "Six months before the whole council voted to take those cameras down, the vote was two to six. I was one of the two. I don't need the room to agree with me before I'm willing to be right.",
    es: "Seis meses antes de que todo el concejo votara por quitar esas cámaras, la votación fue dos a seis. Yo era uno de los dos. No necesito que la sala esté de acuerdo conmigo para estar dispuesto a tener razón.",
    workingTranslation: true,
  },
  {
    id: "deck-plaza",
    en: "The deck park is a nice idea with a two-hundred-and-seven-million-dollar price tag and about seventy-four million committed. One candidate in this race wants the city to put your money into that gap and put it on your tax bill as a bond. I'm not going to do that. I voted no on the seven hundred and fifty thousand dollars in 2024 and I'll vote no on the next one.",
    es: "El parque sobre la autopista es una buena idea con un precio de doscientos siete millones de dólares y unos setenta y cuatro millones comprometidos. Un candidato en esta contienda quiere que la ciudad ponga su dinero en ese hueco y lo cargue a su recibo de impuestos con un bono. Yo no voy a hacer eso. Voté que no a los setecientos cincuenta mil dólares en 2024 y votaré que no al siguiente.",
    workingTranslation: true,
  },
  {
    id: "2023-tax",
    en: "I voted no on that in 2023 and I shouldn't have. It was a motion to go look for savings and I thought it was premature five months into a new term. The next year I voted for the no-new-revenue rate, eight to nothing. But that particular no — I'd take it back.",
    es: "Voté que no en eso, en 2023, y no debí hacerlo. Era una moción para ir a buscar ahorros y me pareció prematura a cinco meses de un concejo nuevo. Al año siguiente voté por la tasa de cero nuevos ingresos, ocho a cero. Pero ese no en particular: me lo retracto.",
    workingTranslation: true,
  },
  {
    id: "money",
    en: "I cap myself at a thousand dollars a contribution and I've never taken a loan. Eight people gave to my campaign this period and six of them are retirees. I'd rather run that campaign and knock the doors myself.",
    es: "Me pongo un tope de mil dólares por contribución y nunca he aceptado un préstamo. Ocho personas le dieron a mi campaña en este periodo y seis de ellas son jubiladas. Prefiero hacer esa campaña y tocar las puertas yo mismo.",
    workingTranslation: true,
  },
  {
    id: "december",
    en: "It's December and it's cold and almost nobody is going to vote. Fifteen hundred people are going to pick who runs this district for four years. Be one of them.",
    es: "Es diciembre, hace frío y casi nadie va a votar. Mil quinientas personas van a decidir quién representa a este distrito durante cuatro años. Sea una de ellas.",
    workingTranslation: true,
  },
];
